import { SkillsGuidedTourContentQuery$data } from '../../graphQLOperations/__generated__/SkillsGuidedTourContentQuery.graphql';
import { StudentSingleSkillQuery$data } from '../../graphQLOperations/__generated__/StudentSingleSkillQuery.graphql';
import { SkillAncestriesId } from '../../models/SkillAncestries';
import { SupportSkills } from '../../models/SupportSkills';
import { removeHighlight } from '../../utils/removeSearchHighlight';

// ── Paths & layout ──────────────────────────────────────────────────────────
export const isGuidedTourPath = (pathname: string) => pathname.startsWith('/guided-tour');

export const GUIDED_TOUR_CONTENT_TOP_MARGIN = 'tw-mt-[139px]';
export const DEFAULT_CONTENT_TOP_MARGIN = 'tw-mt-[90px]';

// ── Demo segments ───────────────────────────────────────────────────────────
// Two designated demo skills, one per grade band the product serves. The "Demo
// Student Skill Experience" button appears only on those two skill pages (matched
// by skill code) and launches that skill's own tour.
export interface GuidedTourSegment {
  slug: string; // friendly URL segment, e.g. 'perimeter-and-area-of-rectangles'
  skillCode: string;
  title: string; // shown in the preview banner + sr-only <h1>
}

const SEGMENT_4_6: GuidedTourSegment = {
  slug: 'perimeter-and-area-of-rectangles',
  skillCode: '153',
  title: 'Perimeter & Area of Rectangles',
};

const SEGMENT_7_ALGEBRA_1: GuidedTourSegment = {
  slug: 'proportion-graphs',
  skillCode: '161',
  title: 'Proportion Graphs',
};

export const GUIDED_TOUR_SEGMENTS: readonly GuidedTourSegment[] = [SEGMENT_4_6, SEGMENT_7_ALGEBRA_1];

// Fallback for an unknown URL slug (the page resolves the demo from the slug).
export const DEFAULT_SEGMENT: GuidedTourSegment = SEGMENT_4_6;

// The teacher page shows the demo button only when the viewed skill is one of the
// two demo skills; returns undefined for any other skill (button hidden).
export const getSegmentBySkillCode = (skillCode?: string | null): GuidedTourSegment | undefined =>
  GUIDED_TOUR_SEGMENTS.find(s => s.skillCode === skillCode);

export const getSegmentBySlug = (slug: string): GuidedTourSegment =>
  GUIDED_TOUR_SEGMENTS.find(s => s.slug === slug) ?? DEFAULT_SEGMENT;

// ── GraphQL adapters ──────────────────────────────────────────────────────────
type TeacherSkillInfo = SkillsGuidedTourContentQuery$data['siteSpecificSkillInfoBySkillCode'];
type AboutTheSkillStateInfo = StudentSingleSkillQuery$data['skillStatesByStudentId'];

export function adaptSkillInfoForAboutTheSkill(info: TeacherSkillInfo | null): AboutTheSkillStateInfo {
  if (!info) return [];
  return [{ skill: info }] as unknown as AboutTheSkillStateInfo;
}

const clean = (value: string | null | undefined) => (value ? removeHighlight(value) : value);

type SearchSkillNode = NonNullable<NonNullable<SkillsGuidedTourContentQuery$data['searchSkills']>['nodes']>[number];

export function adaptSupportSkillsFromSearch(node: SearchSkillNode | null | undefined): {
  highSupportSkills: SupportSkills[];
  midSupportSkills: SupportSkills[];
} {
  const ancestries = node?.skillAncestries ?? [];

  const toSupportSkill = (
    ancestorSkill: NonNullable<NonNullable<(typeof ancestries)[number]>['ancestorSkill']>,
  ): SupportSkills => ({
    id: ancestorSkill.id,
    skill: { id: ancestorSkill.id, name: clean(ancestorSkill.name), code: clean(ancestorSkill.code) },
    skillAssessment: null,
    skillSubstate: null,
    readinessScore: null,
  });

  const bucketBy = (relevanceId: string): SupportSkills[] =>
    ancestries
      .filter(ancestry => ancestry?.skillRelevance?.id === relevanceId && ancestry?.ancestorSkill)
      .map(ancestry => toSupportSkill(ancestry!.ancestorSkill!));

  return {
    highSupportSkills: bucketBy(SkillAncestriesId.HIGH),
    midSupportSkills: bucketBy(SkillAncestriesId.MEDIUM),
  };
}
